/* eslint-disable no-alert */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody, Collapse } from 'reactstrap';
import {
  createPO,
  updatePO,
  getBank,
  liquidPO,
  addToGlJournal,
  editMutationDate,
  printPO,
} from '../api';
import { alertError } from '../utils';
import usePoDetail from './usePoDetail';
import PoHeaderForm from './PoHeaderForm';
import PoItemsTable from './PoItemsTable';
import DetailActions from './DetailActions';
import AddToGlModal from './AddToGlModal';

const REQUIRED = [
  'expenseTypeId',
  'remarks',
  'branchId',
  'dueDate',
  'periodStart',
  'periodEnd',
];

/**
 * The PO Data tab. `selection` is { id, mode } for an existing PO or null
 * for a new one; `setSelection` moves between them (New, or after create).
 */
const PoDataTab = ({ selection, setSelection }) => {
  const expenseTypes = useSelector(
    ({ purchaseOrder }) => purchaseOrder.tujuanPengeluaran,
  );
  const branches = useSelector(({ purchaseOrder }) => purchaseOrder.branches);

  const detail = usePoDetail(selection);
  const {
    mode,
    po,
    form,
    bank,
    setBank,
    mutationDate,
    setMutationDate,
    showErrors,
    setShowErrors,
  } = detail;
  const id = selection?.id;

  const [busy, setBusy] = useState(null);
  const [glBank, setGlBank] = useState(null); // bank details for the GL modal

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      alertError(err);
    } finally {
      setBusy(null);
    }
  };

  const handleNew = () => {
    detail.reset();
    setSelection(null);
  };

  const handleCreate = () => {
    if (REQUIRED.some((key) => !form[key])) {
      alert('please fill out all required fields');
      setShowErrors(true);
      return;
    }
    if (!window.confirm('Are you sure you want to create Purchase Order ?')) {
      return;
    }
    run('save', async () => {
      const created = await createPO(form);
      setSelection({ id: created.id, mode: 'edit' });
    });
  };

  const handleUpdate = () => {
    if (!window.confirm('Are you sure you want to add these data ?')) return;
    run('update', async () => {
      await updatePO(id, form);
      alert('Add data success');
      await detail.reloadAll();
    });
  };

  const requireBankAndDate = () => {
    if (bank.id && mutationDate) return true;
    if (!bank.id && !mutationDate)
      alert('Please input Bank ID and Mutation Date');
    else if (!bank.id) alert('Please input ID Bank');
    else alert('Please input Mutation Date');
    return false;
  };

  const handleLiquid = () => {
    if (!requireBankAndDate()) return;
    if (!window.confirm(`Are you sure want to release ${po.code} ?`)) return;
    run('liquid', async () => {
      await liquidPO(id, { bankId: bank.id, mutationDate });
      alert(`${po.code} has been released`);
    });
  };

  const handleAddToGl = () => {
    if (!requireBankAndDate()) return;
    run('addToGl', async () => setGlBank(await getBank(bank.id)));
  };

  const confirmAddToGl = () =>
    run('confirmGl', async () => {
      try {
        await addToGlJournal(id, { bankId: bank.id, mutationDate });
        alert(`Success add ${po.code} to GL Journal`);
      } finally {
        setGlBank(null);
      }
    });

  const handleEditMutation = () =>
    run('editMutation', async () => {
      await editMutationDate(id, mutationDate);
      alert('Edit Mutation Date Success');
    });

  const handlePrint = () => run('print', () => printPO(id));

  const handleBankChange = (bankId) =>
    getBank(bankId).then(setBank).catch(alertError);

  const actionHandlers = {
    save: handleCreate,
    update: handleUpdate,
    liquid: handleLiquid,
    addToGl: handleAddToGl,
    editMutation: handleEditMutation,
  };

  return (
    <Card>
      <CardBody>
        <PoHeaderForm
          po={po}
          form={form}
          setField={detail.setField}
          setForm={detail.setForm}
          mode={mode}
          invalid={(key) => showErrors && !form[key]}
          expenseTypes={expenseTypes}
          branches={branches}
          banks={detail.banks}
          bank={bank}
          onBankChange={handleBankChange}
          mutationDate={mutationDate}
          setMutationDate={setMutationDate}
        />
        <AddToGlModal
          bank={glBank}
          poCode={po?.code}
          mutationDate={mutationDate}
          busy={busy === 'confirmGl'}
          onConfirm={confirmAddToGl}
          onCancel={() => setGlBank(null)}
        />
        <Collapse isOpen={mode.showItems}>
          <PoItemsTable
            poId={id}
            items={po?.poItems ?? []}
            readOnly={mode.readOnly}
            onChanged={detail.reloadItems}
          />
        </Collapse>
      </CardBody>
      <DetailActions
        mode={mode}
        hasId={Boolean(id)}
        busy={busy}
        onNew={handleNew}
        onAction={actionHandlers[mode.action]}
        onPrint={handlePrint}
      />
    </Card>
  );
};

export default PoDataTab;
